import React from 'react';

// Per-weekday "grade de horário" for a liberar-visita período: each allowed weekday carries its
// own (possibly different) HH:MM–HH:MM window. Distinct from the single-window ScheduleInput.
// Emits the shape the backend + mobile apps use: [{ day, windows: [{ start, end }] }].
// day = 0(Dom)..6(Sáb), matching Go's time.Weekday and the Android picker.

export interface VisitWindow {
  start: string; // "HH:MM"
  end: string;   // "HH:MM"
}
export interface VisitDaySchedule {
  day: number;
  windows: VisitWindow[];
}

const WEEKDAY_SHORT = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WEEKDAY_LABEL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

interface Props {
  value: VisitDaySchedule[];
  onChange: (schedule: VisitDaySchedule[]) => void;
  darkMode?: boolean;
}

const VisitGradeInput: React.FC<Props> = ({ value, onChange, darkMode }) => {
  const byDay = new Map<number, VisitWindow>();
  value.forEach((d) => {
    if (d.windows && d.windows.length > 0) byDay.set(d.day, d.windows[0]);
  });

  const emit = (map: Map<number, VisitWindow>) => {
    const next = Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([day, w]) => ({ day, windows: [w] }));
    onChange(next);
  };

  const toggleDay = (day: number) => {
    const map = new Map(byDay);
    if (map.has(day)) map.delete(day);
    else map.set(day, { start: '08:00', end: '18:00' });
    emit(map);
  };

  const setWindow = (day: number, patch: Partial<VisitWindow>) => {
    const map = new Map(byDay);
    const cur = map.get(day) ?? { start: '08:00', end: '18:00' };
    map.set(day, { ...cur, ...patch });
    emit(map);
  };

  const sortedDays = Array.from(byDay.keys()).sort((a, b) => a - b);

  const timeInputCls = darkMode
    ? 'bg-gray-700 border-gray-600 text-gray-100'
    : 'bg-white border-gray-300 text-gray-800';

  return (
    <div className="space-y-3">
      <div className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Dias e horários
      </div>

      {/* Weekday toggles */}
      <div className="grid grid-cols-7 gap-1.5">
        {WEEKDAY_SHORT.map((label, day) => {
          const on = byDay.has(day);
          return (
            <button
              key={day}
              type="button"
              aria-pressed={on}
              onClick={() => toggleDay(day)}
              className={`h-10 rounded-lg text-sm font-semibold transition-all ${
                on
                  // Was a near-black navy that belonged to no other control on the screen.
                  ? 'bg-blue-600 text-white'
                  : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Per-day time rows */}
      {sortedDays.map((day) => {
        const w = byDay.get(day)!;
        return (
          <div key={day} className="flex items-center gap-2">
            <span className={`w-20 text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {WEEKDAY_LABEL[day]}
            </span>
            <input
              type="time"
              value={w.start}
              onChange={(e) => setWindow(day, { start: e.target.value })}
              className={`px-2 py-1.5 rounded-md border text-sm ${timeInputCls}`}
            />
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>–</span>
            <input
              type="time"
              value={w.end}
              onChange={(e) => setWindow(day, { end: e.target.value })}
              className={`px-2 py-1.5 rounded-md border text-sm ${timeInputCls}`}
            />
          </div>
        );
      })}

      {sortedDays.length === 0 && (
        <p className={`text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
          Selecione ao menos um dia.
        </p>
      )}
    </div>
  );
};

export default VisitGradeInput;

// Shared helper: is `now` within an allowed window for its weekday (local time, end-exclusive).
// Display-only — mirrors the backend/mobile evaluation for the "dentro/fora do horário" indicator.
export function isWithinVisitGradeNow(schedule: VisitDaySchedule[] | null | undefined, now = new Date()): boolean {
  if (!schedule || schedule.length === 0) return false;
  const day = now.getDay(); // 0..6
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (s: string) => {
    const [h, m] = s.split(':').map((n) => parseInt(n, 10));
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
  };
  return schedule.some(
    (d) =>
      d.day === day &&
      (d.windows || []).some((w) => {
        const s = toMin(w.start);
        const e = toMin(w.end);
        return Number.isFinite(s) && Number.isFinite(e) && nowMin >= s && nowMin < e;
      }),
  );
}

// A weekday index outside 0..6 cannot come from the pickers and the backend rejects it, but it can
// still arrive on an older row or a hand-written payload — and this formatter runs inside the
// Liberados card and summarizeLiberacoes, so an index lookup that throws takes a whole screen down
// instead of degrading one label. Mirrors Android's `DIAS.getOrElse(it.day) { "?" }`.
const weekdayShortLabel = (day: number): string => WEEKDAY_LABEL[day]?.slice(0, 3) ?? '?';

// Compact summary: "Seg, Qua, Sex · 08:00–18:00" when uniform, else per-day.
export function formatVisitGrade(schedule: VisitDaySchedule[] | null | undefined): string {
  if (!schedule || schedule.length === 0) return '';
  const days = schedule.filter((d) => d.windows && d.windows.length > 0).sort((a, b) => a.day - b.day);
  if (days.length === 0) return '';
  const uniform =
    days.every((d) => d.windows.length === 1) &&
    new Set(days.map((d) => `${d.windows[0].start}-${d.windows[0].end}`)).size === 1;
  if (uniform) {
    const w = days[0].windows[0];
    return `${days.map((d) => weekdayShortLabel(d.day)).join(', ')} · ${w.start}–${w.end}`;
  }
  return days
    .map((d) => `${weekdayShortLabel(d.day)} ${d.windows.map((w) => `${w.start}–${w.end}`).join('/')}`)
    .join(' · ');
}
